import './PDFIFrameRenderer.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DocRenderer } from '@cyntler/react-doc-viewer';
import FatalError from '@molecules/FatalError';
import FatalErrorIcon from '@molecules/FatalError/FatalErrorIcon';
import { ConfigService } from '@/configuration/services/configService';
import $api, { API_URL } from '@/http';
import Preloader from '@atoms/Preloader';
import Button from '@atoms/Button';
import { MdRefresh } from 'react-icons/md';
import clsx from 'clsx';

const PDFIFrameRenderer: DocRenderer = ({ mainState: { currentDocument } }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [blobUrl, setBlobUrl] = useState<string | null>(null);

    // храним последний blob, чтобы корректно его ревокать при обновлениях
    const lastBlobUrlRef = useRef<string | null>(null);
    // защита от гонок при быстрых переключениях документов
    const requestIdRef = useRef(0);

    const revokeBlob = (url: string | null) => {
        if (url) {
            URL.revokeObjectURL(url);
        }
    };

    const fetchDocument = useCallback(
        async (controller: AbortController) => {
            const uriRaw = currentDocument?.uri;

            if (!uriRaw) {
                setIsLoading(false);
                setError('Документ не найден');
                setBlobUrl(null);
                return;
            }

            // инкрементируем id запроса
            const reqId = ++requestIdRef.current;

            // сбрасываем состояние перед загрузкой
            setIsLoading(true);
            setError(null);

            // ревокаем предыдущий blob сразу, чтобы не держать память
            revokeBlob(lastBlobUrlRef.current);
            lastBlobUrlRef.current = null;
            setBlobUrl(null);

            try {
                const uri = uriRaw.startsWith(API_URL) ? uriRaw.substring(API_URL.length) : uriRaw;

                const response = await $api.get(uri, {
                    responseType: 'arraybuffer',
                    signal: controller.signal,
                });

                const blob = new Blob([response.data], {
                    type: response.headers['content-type'] || 'application/octet-stream',
                });
                const url = URL.createObjectURL(blob);

                // если за время загрузки пользователь успел открыть другой документ — игнорируем результат
                if (reqId !== requestIdRef.current) {
                    URL.revokeObjectURL(url);
                    return;
                }

                lastBlobUrlRef.current = url;
                setBlobUrl(url);
                setIsLoading(false);
                setError(null);
            } catch (e: any) {
                if (controller.signal.aborted) return;

                setIsLoading(false);
                setError(e?.toString?.() ?? 'Неизвестная ошибка');
                setBlobUrl(null);
            }
        },
        [currentDocument?.uri],
    );

    const handleRetry = useCallback(() => {
        const controller = new AbortController();
        fetchDocument(controller);
    }, [fetchDocument]);

    useEffect(() => {
        const controller = new AbortController();
        fetchDocument(controller);
        return () => controller.abort();
    }, [fetchDocument]);

    // ревокаем blob при размонтировании/смене blobUrl
    useEffect(() => {
        return () => {
            revokeBlob(lastBlobUrlRef.current);
            lastBlobUrlRef.current = null;
        };
    }, []);

    if (!currentDocument) {
        return (
            <FatalError
                icon={<FatalErrorIcon variant="default" />}
                title="Не удалось загрузить файл на просмотр"
                description="Документ не найден"
            />
        );
    }

    if (isLoading) {
        return (
            <div className={clsx('pdf-renderer-iframe', 'pdf-renderer-loading')}>
                <Preloader size="l" label="Загрузка документа..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className={clsx('pdf-renderer-iframe', 'pdf-renderer-error')}>
                <FatalError
                    icon={<FatalErrorIcon variant="default" />}
                    title="Не удалось загрузить файл на просмотр"
                    description={error}
                />
                <Button
                    type="button"
                    className="error-page__refresh-button"
                    onClick={handleRetry}
                    startAdornment={<MdRefresh size="24" />}
                >
                    Повторить попытку
                </Button>
            </div>
        );
    }

    return (
        <div className="pdf-renderer-iframe">
            <iframe
                src={blobUrl!}
                title={currentDocument.fileName}
                width="100%"
                height="100%"
                onError={() => {
                    setError('Ошибка отображения документа в IFrame');
                    setIsLoading(false);
                }}
            />
        </div>
    );
};

PDFIFrameRenderer.fileLoader = ({ fileLoaderComplete }) => {
    // Не делаем сетевые запросы — только завершаем спиннер DocViewer'а.
    fileLoaderComplete();
};

const config = ConfigService.get();
const pdfPreviewFileTypes = config.application.pdfPreviewFileTypes;
// prettier-ignore
PDFIFrameRenderer.fileTypes = pdfPreviewFileTypes ?? [
    'csv', 'doc', 'docm', 'docx', 'fodt', 'html', 'odp', 'ods', 'odt', 'odf','xodt', 'pdf', 'txt',
    'pot', 'potm', 'potx', 'ppm', 'pps', 'ppam', 'ppsm', 'ppsx', 'ppt', 'pptm', 'pptx', 'rtf', 'svg',
    'xhtml', 'xla', 'xlam', 'xlc', 'xlm', 'xls', 'xlsm', 'xlsx', 'xlt', 'xltm', 'xltx', 'ott',
    'dotm', 'dotx'
];
PDFIFrameRenderer.weight = 1;

export default PDFIFrameRenderer;
